import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../Utils/baseQueryWithReauth";
import { PromptLibraryItem } from "../interfaces/slices.interfaces";
import { SessionData } from "../components/AIPlayground/Interfaces/Sessions.interfaces";

// Define the API slice
export const aiHubApi = createApi({
  reducerPath: "aiHub",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["UserSessions"],
  endpoints: (builder) => ({
    // 2. Save a new session
    SavePromptPlaygroundUserSession: builder.mutation<
      string,
      {
        title: string;
        description: string;
        query: string;
        prompt: string;
        isActiveSchedule: boolean;
        scheduleFrequency: string;
        isPublic: boolean;
      }
    >({
      query: (payload) => ({
        url: "AIPlayground/SavePromptPlaygroundUserSession",
        method: "POST",
        body: payload,
        responseHandler: (response) => response.text() // Handle response as text to get the session ID
      }),
      invalidatesTags: ["UserSessions"]
    }),

    // 3. Get schedules
    GetUserPromptPlaygroundSchedules: builder.query<
      {
        sessionName: string;
        frequency: "daily" | "weekly" | "monthly";
        status: "active" | "inactive";
        timestamp: string;
      }[],
      void
    >({
      query: () => "AIPlayground/GetUserPromptPlaygroundSchedules"
    }),

    // 4. Update schedules
    UpdateUserPromptPlaygroundSchedules: builder.mutation<
      boolean,
      { sessionName: string; frequency: string; status: string }[]
    >({
      query: (body) => ({
        url: "AIPlayground/UpdateUserPromptPlaygroundSchedules",
        method: "POST",
        body
      })
    }),

    // 5. Existing query execution API
    executeQueryPrompt: builder.mutation<{ images: []; text: string }, { query: string; prompt: string }>({
      query: (payload) => ({
        url: "AIPlayground/RunPromptPlaygroundSession",
        method: "POST",
        body: payload
      }),
      transformResponse: (response: any) => response
    }),
    // 6. Get prompt library data from the backend API.
    GetPromptLibrary: builder.query<PromptLibraryItem[], void>({
      query: () => "aiplayground/getpromptlibrary"
    }),
    // 7. Get user prompt playground sessions
    GetPromptPlaygroundUserSessions: builder.query<SessionData[], void>({
      query: () => "AIPlayground/GetPromptPlaygroundUserSessions",
      providesTags: ["UserSessions"]
    }),
    // 8. update saved user prompt playground sessions
    UpdatePromptPlaygroundUserSession: builder.mutation<boolean, SessionData>({
      query: (sessionData) => ({
        url: "AIPlayground/UpdatePromptPlaygroundUserSession",
        method: "POST",
        body: sessionData
      }),
      invalidatesTags: ["UserSessions"]
    }),
    // 9. delete saved user prompt playground sessions
    DeletePromptPlaygroundUserSession: builder.mutation<boolean, SessionData>({
      query: (sessionData) => ({
        url: "AIPlayground/DeletePromptPlaygroundUserSession",
        method: "POST",
        body: sessionData
      }),
      invalidatesTags: ["UserSessions"]
    }),
    // 10. Bulk update endpoint for saving multiple prompt playground sessions at once
    BulkUpdatePromptPlaygroundUserSessions: builder.mutation<boolean, SessionData[]>({
      query: (sessions) => ({
        url: "AIPlayground/BulkUpdatePromptPlaygroundUserSessions",
        method: "POST",
        body: sessions
      }),
      invalidatesTags: ["UserSessions"]
    }),
    // 11. Log User Activity
    LogPromptLibraryClickActivity: builder.mutation<void, { promptId: string; eventType: "Read" | "Execute" | "Open" }>(
      {
        query: ({ promptId, eventType }) => ({
          url: "AIPlayground/LogPromptLibraryClickActivity",
          method: "POST",
          body: { promptId, eventType }
        })
      }
    ),
    // 12. Get Copilot Agent Chat Scenarios
    GetCopilotAgentChatScenarios: builder.query<
      {
        scenarioId: number;
        scenarioGrouping: string;
        scenarioName: string;
        description: string;
        additionalInformation: string;
        suggestedPrompts: string;
        state: string;
        promptCategory: string;
      }[],
      void
    >({
      query: () => "aiplayground/GetCopilotAgentChatScenarios"
    }),

    // 13. Save Copilot User Message Reaction (Like/Dislike)
    SaveCopilotUserMessageReaction: builder.mutation<
      boolean,
      {
        messageId: string;
        sessionId: string;
        action: boolean | null; // true = like, false = dislike, null = toggle off
        whatWentWell?: string | null; // Rich feedback: what went well
        whatDidNotGoWell?: string | null; // Rich feedback: what did not go well
        goodToHave?: string | null; // Rich feedback: good to have suggestions
        environment?: string | null; // Environment where feedback was submitted (e.g., PPE, PROD)
      }
    >({
      query: (payload) => ({
        url: "AIPlayground/SaveCopilotUserMessageReaction",
        method: "POST",
        body: payload
      })
    }),

    // 14. Save Copilot User Session Feedback (Star rating + text feedback)
    SaveCopilotUserSessionFeedback: builder.mutation<
      boolean,
      {
        sessionId: string;
        rating: number | null; // 1-5 star rating
        feedback: string | null; // Text feedback
        environment?: string | null; // Environment where feedback was submitted (e.g., PPE, PROD)
      }
    >({
      query: (payload) => ({
        url: "AIPlayground/SaveCopilotUserSessionFeedback",
        method: "POST",
        body: payload
      })
    }),

    // 15. Create Share Link - stores prompt playground data and returns a short share ID
    CreatePromptPlaygroundShareLink: builder.mutation<
      { shareId: string },
      {
        query: string;
        prompt: string;
        results: string;
        resultImages: string[];
      }
    >({
      query: (payload) => ({
        url: "AIPlayground/CreateShareLink",
        method: "POST",
        body: payload
      })
    }),

    // 16. Get Shared Prompt Playground Data by share ID
    GetPromptPlaygroundSharedData: builder.query<
      {
        query: string;
        prompt: string;
        results: string;
        resultImages: string[];
      },
      string
    >({
      query: (shareId) => `AIPlayground/GetSharedData/${shareId}`
    })
  })
});

// Export hooks for API endpoints
export const {
  useExecuteQueryPromptMutation,
  useGetPromptLibraryQuery,
  useGetUserPromptPlaygroundSchedulesQuery,
  useSavePromptPlaygroundUserSessionMutation,
  useGetPromptPlaygroundUserSessionsQuery,
  useUpdateUserPromptPlaygroundSchedulesMutation,
  useUpdatePromptPlaygroundUserSessionMutation,
  useDeletePromptPlaygroundUserSessionMutation,
  useBulkUpdatePromptPlaygroundUserSessionsMutation,
  useLogPromptLibraryClickActivityMutation,
  useGetCopilotAgentChatScenariosQuery,
  useSaveCopilotUserMessageReactionMutation,
  useSaveCopilotUserSessionFeedbackMutation,
  useCreatePromptPlaygroundShareLinkMutation,
  useLazyGetPromptPlaygroundSharedDataQuery
} = aiHubApi;
